import { useState } from 'react'
import { Alert, App, Button, Descriptions, Space, Table, Tabs, Tag, Tooltip, Typography } from 'antd'
import {
  DollarOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PercentageOutlined,
  PlusCircleOutlined,
  PoweroffOutlined,
  RollbackOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDate, formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue, isNegative } from '@/shared/lib/money'
import { TWO_COLUMNS } from '@/shared/ui/descriptionColumns'
import { PageHeader } from '@/shared/ui/PageHeader'
import { StatTiles, type StatTile } from '@/shared/ui/StatTiles'
import { RowActions } from '@/shared/ui/table/RowActions'
import { CapitalTxnModal, type CapitalMode } from '@/features/stakeholder/CapitalTxnModal'
import { DistributionsTable } from '@/features/stakeholder/DistributionsTable'
import { ReasonModal } from '@/features/stakeholder/ReasonModal'
import { ShareAmount, SignedAmount } from '@/features/stakeholder/ShareAmount'
import { ShareChangeModal } from '@/features/stakeholder/ShareChangeModal'
import { StakeholderAttachmentsCard } from '@/features/stakeholder/StakeholderAttachmentsCard'
import { StakeholderFormModal } from '@/features/stakeholder/StakeholderFormModal'
import {
  KIND_COLOR,
  STATUS_COLOR,
  bpToPercent,
  fetchLedger,
  fetchPerformance,
  fetchShares,
  fetchStakeholder,
  fetchStakeholderDistributions,
  reverseTxn,
  setStakeholderActive,
  type LedgerTxn,
  type Stakeholder,
} from '@/features/stakeholder/stakeholderApi'

export function StakeholderDetailPage() {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const { id = '' } = useParams<{ id: string }>()
  const [editing, setEditing] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [capitalMode, setCapitalMode] = useState<CapitalMode | null>(null)
  const [reversing, setReversing] = useState<LedgerTxn | null>(null)

  const stakeholder = useQuery({
    queryKey: queryKeys.stakeholder.detail(id),
    queryFn: ({ signal }) => fetchStakeholder(id, signal),
  })
  const ledger = useQuery({
    queryKey: queryKeys.stakeholder.ledger(id),
    queryFn: ({ signal }) => fetchLedger(id, signal),
  })
  const distributions = useQuery({
    queryKey: queryKeys.stakeholder.distributions(id),
    queryFn: ({ signal }) => fetchStakeholderDistributions(id, signal),
  })
  const shares = useQuery({
    queryKey: queryKeys.stakeholder.shares(id),
    queryFn: ({ signal }) => fetchShares(id, signal),
  })
  const performance = useQuery({
    queryKey: queryKeys.stakeholder.performance(id),
    queryFn: ({ signal }) => fetchPerformance(id, signal),
  })

  const toggle = useMutation({
    mutationFn: (active: boolean) => setStakeholderActive(id, active),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all }),
    onError: (error) => void message.error(translateError(error, t)),
  })

  if (stakeholder.isError) {
    return <Alert type="error" showIcon message={translateError(stakeholder.error, t)} />
  }
  const data = stakeholder.data
  const inactiveHint = data && !data.active ? t('stakeholder.inactiveHint') : undefined

  return (
    <>
      <PageHeader
        title={data ? `${data.name} · ${data.code}` : t('common.loading')}
        description={data ? `${t(`stakeholder.type.${data.stakeholderType}`)} · ${scopeLabel(data, t)}` : undefined}
        extra={
          <Space wrap>
            {data && !data.active ? <Tag>{t('stakeholder.state.inactive')}</Tag> : null}
            <Can permission="stakeholder.manage">
              <Tooltip title={inactiveHint}>
                <Button icon={<PlusCircleOutlined />} disabled={!data?.active} onClick={() => setCapitalMode('record')}>
                  {t('stakeholder.capital.record')}
                </Button>
              </Tooltip>
            </Can>
            <Can permission="stakeholder.pay">
              <Tooltip title={inactiveHint}>
                <Button icon={<MinusCircleOutlined />} disabled={!data?.active} onClick={() => setCapitalMode('withdraw')}>
                  {t('stakeholder.capital.withdraw')}
                </Button>
              </Tooltip>
            </Can>
            <Can permission="stakeholder.pay">
              <Tooltip title={inactiveHint}>
                <Button icon={<WalletOutlined />} disabled={!data?.active} onClick={() => setCapitalMode('advance')}>
                  {t('stakeholder.capital.advance')}
                </Button>
              </Tooltip>
            </Can>
            {data && data.summary.debt.amount > 0 ? (
              <Can permission="stakeholder.pay">
                <Button icon={<DollarOutlined />} onClick={() => setCapitalMode('repay')}>
                  {t('stakeholder.capital.repay')}
                </Button>
              </Can>
            ) : null}
            <Can permission="stakeholder.manage">
              <Button icon={<PercentageOutlined />} disabled={!data} onClick={() => setSharing(true)}>
                {t('stakeholder.share.change')}
              </Button>
            </Can>
            <Can permission="stakeholder.manage">
              <Button icon={<EditOutlined />} disabled={!data} onClick={() => setEditing(true)}>
                {t('stakeholder.action.edit')}
              </Button>
            </Can>
            <Can permission="stakeholder.manage">
              <Button
                icon={<PoweroffOutlined />}
                disabled={!data}
                loading={toggle.isPending}
                onClick={() => data && toggle.mutate(!data.active)}
              >
                {data?.active ? t('stakeholder.action.deactivate') : t('stakeholder.action.activate')}
              </Button>
            </Can>
          </Space>
        }
      />

      {data ? <SummaryCards stakeholder={data} /> : null}

      <Tabs
        items={[
          {
            key: 'profile',
            label: t('stakeholder.tab.profile'),
            children: data ? <Profile stakeholder={data} /> : null,
          },
          {
            key: 'ledger',
            label: t('stakeholder.tab.ledger'),
            children: (
              <Table<LedgerTxn>
                size="small"
                rowKey="id"
                loading={ledger.isPending}
                pagination={false}
                scroll={{ x: 1300 }}
                dataSource={[...(ledger.data ?? [])]}
                locale={{ emptyText: t('stakeholder.ledger.empty') }}
                columns={[
                  { key: 'date', title: t('stakeholder.ledger.date'), width: 110, render: (_, row) => formatDate(row.txnDate) },
                  {
                    key: 'kind',
                    title: t('stakeholder.ledger.kind'),
                    width: 220,
                    render: (_, row) => (
                      <Space size={4} wrap>
                        <Tag color={txnKindColor(row.kind)}>{t(`stakeholder.txnKind.${row.kind}`)}</Tag>
                        {row.kind === 'REVERSAL' ? t(`stakeholder.txnKind.${row.category}`) : null}
                        {row.category === 'DEBT_OFFSET' ? (
                          <Typography.Text type="secondary">{formatMoneyValue(row.amount)}</Typography.Text>
                        ) : null}
                        {row.reversedById ? <Tag>{t('stakeholder.ledger.reversed')}</Tag> : null}
                      </Space>
                    ),
                  },
                  { key: 'period', title: t('stakeholder.distribution.period'), width: 100, render: (_, row) => row.periodCode ?? t('common.dash') },
                  { key: 'capital', title: t('stakeholder.ledger.capital'), width: 150, align: 'right', render: (_, row) => signed(row.capital) },
                  { key: 'payable', title: t('stakeholder.ledger.payable'), width: 150, align: 'right', render: (_, row) => signed(row.payable) },
                  {
                    key: 'account',
                    title: t('stakeholder.ledger.account'),
                    width: 200,
                    render: (_, row) => (row.account ? `${row.account.code} · ${seededName('accounts', row.account.code, row.account.name, t)}` : t('common.dash')),
                  },
                  { key: 'reference', title: t('stakeholder.capital.reference'), width: 140, render: (_, row) => row.reference ?? t('common.dash') },
                  { key: 'note', title: t('stakeholder.capital.note'), width: 200, render: (_, row) => row.note ?? t('common.dash') },
                  { key: 'by', title: t('stakeholder.ledger.by'), width: 150, render: (_, row) => row.createdBy.name },
                  {
                    key: 'actions',
                    title: '',
                    width: 56,
                    align: 'center',
                    fixed: 'right',
                    render: (_, row) => (
                      <RowActions
                        items={[
                          {
                            key: 'reverse',
                            label: t('stakeholder.action.reverse'),
                            icon: <RollbackOutlined />,
                            danger: true,
                            permission: PAYS_OUT.has(row.kind) ? 'stakeholder.pay' : 'stakeholder.manage',
                            hidden:
                              row.kind === 'REVERSAL' ||
                              row.kind === 'PROFIT_DISTRIBUTION' ||
                              row.kind === 'LOSS_ALLOCATION' ||
                              row.kind === 'DEBT_OFFSET' ||
                              row.reversedById !== null,
                            onClick: () => setReversing(row),
                          },
                        ]}
                      />
                    ),
                  },
                ]}
              />
            ),
          },
          {
            key: 'distributions',
            label: t('stakeholder.tab.distributions'),
            children: (
              <DistributionsTable rows={distributions.data ?? []} loading={distributions.isPending} showStakeholder={false} />
            ),
          },
          {
            key: 'performance',
            label: t('stakeholder.tab.performance'),
            children: (
              <>
                <Typography.Paragraph type="secondary">{t('stakeholder.performance.note')}</Typography.Paragraph>
                <Table
                  size="small"
                  rowKey="periodId"
                  loading={performance.isPending}
                  pagination={false}
                  scroll={{ x: 1330 }}
                  dataSource={[...(performance.data ?? [])]}
                  locale={{ emptyText: t('stakeholder.performance.empty') }}
                  columns={[
                    { key: 'period', title: t('stakeholder.distribution.period'), width: 100, render: (_, row) => row.periodCode },
                    { key: 'revenue', title: t('stakeholder.profit.revenue'), align: 'right', render: (_, row) => formatMoneyValue(row.profit.revenue) },
                    {
                      key: 'expenses',
                      title: t('stakeholder.profit.expenses'),
                      align: 'right',
                      render: (_, row) =>
                        formatMoneyValue({
                          amount: row.profit.cogs.amount + row.profit.operating.amount + row.profit.other.amount,
                          currency: row.profit.net.currency,
                        }),
                    },
                    { key: 'net', title: t('stakeholder.profit.net'), align: 'right', render: (_, row) => <SignedAmount value={row.profit.net} /> },
                    {
                      key: 'share',
                      title: t('stakeholder.distribution.share'),
                      align: 'right',
                      render: (_, row) => t('stakeholder.percent', { value: bpToPercent(row.shareBp) }),
                    },
                    { key: 'expected', title: t('stakeholder.performance.expected'), align: 'right', render: (_, row) => <SignedAmount value={row.expected} /> },
                    {
                      key: 'kind',
                      title: t('stakeholder.distribution.kind'),
                      render: (_, row) => <Tag color={KIND_COLOR[row.kind]}>{t(`stakeholder.distributionKind.${row.kind}`)}</Tag>,
                    },
                    {
                      key: 'status',
                      title: t('stakeholder.distribution.status'),
                      render: (_, row) =>
                        row.status ? <Tag color={STATUS_COLOR[row.status]}>{t(`stakeholder.status.${row.status}`)}</Tag> : t('common.dash'),
                    },
                    {
                      key: 'approved',
                      title: t('stakeholder.distribution.approved'),
                      align: 'right',
                      render: (_, row) => (row.approved ? <ShareAmount value={row.approved} kind={row.kind} /> : t('common.dash')),
                    },
                    {
                      key: 'paid',
                      title: t('stakeholder.distribution.paid'),
                      align: 'right',
                      render: (_, row) => (row.kind === 'LOSS' ? t('common.dash') : formatMoneyValue(row.paid)),
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'shares',
            label: t('stakeholder.tab.shares'),
            children: (
              <Table
                size="small"
                rowKey="id"
                loading={shares.isPending}
                pagination={false}
                dataSource={[...(shares.data ?? [])]}
                columns={[
                  { key: 'from', title: t('stakeholder.share.effectiveFrom'), render: (_, row) => formatDate(row.effectiveFrom) },
                  {
                    key: 'share',
                    title: t('stakeholder.field.share'),
                    align: 'right',
                    render: (_, row) => t('stakeholder.percent', { value: bpToPercent(row.shareBp) }),
                  },
                  { key: 'reason', title: t('actions.reason'), render: (_, row) => row.reason ?? t('stakeholder.share.initial') },
                  { key: 'by', title: t('stakeholder.ledger.by'), render: (_, row) => row.createdBy.name },
                  { key: 'at', title: t('stakeholder.share.recordedAt'), render: (_, row) => formatDateTime(row.createdAt) },
                ]}
              />
            ),
          },
          { key: 'attachments', label: t('stakeholder.tab.attachments'), children: <StakeholderAttachmentsCard stakeholderId={id} /> },
        ]}
      />

      <StakeholderFormModal open={editing} stakeholder={data ?? null} onClose={() => setEditing(false)} onSaved={() => undefined} />
      <ShareChangeModal stakeholder={sharing && data ? data : null} onClose={() => setSharing(false)} />
      <CapitalTxnModal stakeholder={capitalMode && data ? data : null} mode={capitalMode ?? 'record'} onClose={() => setCapitalMode(null)} />
      <ReasonModal
        open={reversing !== null}
        title={t('stakeholder.reverseTxn.title')}
        warning={t('stakeholder.reverseTxn.warning')}
        okText={t('stakeholder.action.reverse')}
        onSubmit={(reason) => reverseTxn(id, reversing?.id ?? '', reason)}
        onClose={() => setReversing(null)}
      />
    </>
  )
}

type Translate = ReturnType<typeof useTranslation>['t']

function scopeLabel(stakeholder: Stakeholder, t: Translate): string {
  return stakeholder.scope ? `${stakeholder.scope.code} · ${stakeholder.scope.name}` : t('stakeholder.scope.company')
}

const PAYS_OUT: ReadonlySet<LedgerTxn['kind']> = new Set(['WITHDRAWAL', 'PROFIT_PAYMENT', 'ADVANCE'])

function txnKindColor(kind: LedgerTxn['kind']): string | undefined {
  if (kind === 'REVERSAL') return 'orange'
  if (kind === 'LOSS_ALLOCATION') return 'red'
  if (kind === 'ADVANCE') return 'gold'
  if (kind === 'DEBT_REPAYMENT' || kind === 'DEBT_OFFSET') return 'green'
  return undefined
}

function signed(value: { amount: number; currency: string }): string {
  if (value.amount === 0) return ''
  return value.amount > 0 ? `+${formatMoneyValue(value)}` : formatMoneyValue(value)
}

function SummaryCards({ stakeholder }: { stakeholder: Stakeholder }) {
  const { t } = useTranslation()
  const s = stakeholder.summary
  const cards: StatTile[] = [
    { key: 'ownership', title: t('stakeholder.summary.ownership'), value: t('stakeholder.percent', { value: bpToPercent(s.ownershipBp) }) },
    { key: 'capital', title: t('stakeholder.summary.initialInvestment'), value: formatMoneyValue(s.initialInvestment) },
    { key: 'additional', title: t('stakeholder.summary.additionalInvestment'), value: formatMoneyValue(s.additionalInvestment) },
    { key: 'profitShare', title: t('stakeholder.summary.profitShare'), value: formatMoneyValue(s.profitShare) },
    { key: 'lossShare', title: t('stakeholder.summary.lossShare'), value: formatMoneyValue(s.lossShare), danger: s.lossShare.amount > 0 },
    { key: 'withdrawn', title: t('stakeholder.summary.withdrawn'), value: formatMoneyValue(s.withdrawn) },
    {
      key: 'balance',
      title: t('stakeholder.summary.currentBalance'),
      value: formatMoneyValue(s.currentBalance),
      danger: isNegative(s.currentBalance),
    },
    {
      key: 'payable',
      title: t('stakeholder.summary.payable'),
      value: formatMoneyValue(s.payable),
      danger: isNegative(s.payable),
    },
    { key: 'debt', title: t('stakeholder.summary.debt'), value: formatMoneyValue(s.debt), danger: s.debt.amount > 0 },
    ...(s.advances.amount > 0
      ? [{ key: 'advances', title: t('stakeholder.summary.advances'), value: formatMoneyValue(s.advances) }]
      : []),
    ...(s.debtRepaid.amount > 0
      ? [{ key: 'debtRepaid', title: t('stakeholder.summary.debtRepaid'), value: formatMoneyValue(s.debtRepaid) }]
      : []),
  ]
  return (
    <>
      <StatTiles tiles={cards} />
      <Typography.Paragraph type="secondary">
        {t('stakeholder.summary.breakdown', {
          capital: formatMoneyValue(s.capital),
          payable: formatMoneyValue(s.payable),
          adjustments: formatMoneyValue(s.adjustments),
          withdrawals: formatMoneyValue(s.withdrawals),
          profitPaid: formatMoneyValue(s.profitPaid),
        })}
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary">{t('stakeholder.summary.debtHint')}</Typography.Paragraph>
    </>
  )
}

function Profile({ stakeholder }: { stakeholder: Stakeholder }) {
  const { t } = useTranslation()
  const dash = t('common.dash')
  return (
    <Descriptions size="small" column={TWO_COLUMNS} bordered>
      <Descriptions.Item label={t('stakeholder.field.code')}>{stakeholder.code}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.kind')}>{t(`stakeholder.kind.${stakeholder.kind}`)}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.scope')}>{scopeLabel(stakeholder, t)}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.startDate')}>{formatDate(stakeholder.startDate)}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.phone')}>{stakeholder.phone ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.whatsapp')}>{stakeholder.whatsapp ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.email')}>{stakeholder.email ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.idDocument')}>{stakeholder.idDocument ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.address')} span={2}>{stakeholder.address ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.bankName')}>{stakeholder.bankName ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.bankAccount')}>{stakeholder.bankAccount ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.paymentDetails')} span={2}>{stakeholder.paymentDetails ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.agreementRef')}>{stakeholder.agreementRef ?? dash}</Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.status')}>
        {stakeholder.active ? t('stakeholder.state.active') : t('stakeholder.state.inactive')}
      </Descriptions.Item>
      <Descriptions.Item label={t('stakeholder.field.notes')} span={2}>{stakeholder.notes ?? dash}</Descriptions.Item>
    </Descriptions>
  )
}
